import React from 'react';
import ReactDOM from 'react-dom/client';
import { registerSW } from 'virtual:pwa-register';
import App from './src/app';
import ClientUpgradeGate from './src/components/ClientUpgradeGate';
import { ensureStoreReady } from './src/native/assetStore';
import { isNativeApp } from './src/utils/platform';

import './src/assets/fonts/Inter/Inter.css';

// With registerType: 'autoUpdate' the new service worker calls skipWaiting()
// and clientsClaim(), but the open page keeps running the old in-memory bundle
// until something reloads it. registerSW({ immediate: true }) wires up that
// missing reload so returning clients (warm mobile tabs / installed PWAs) pick
// up the latest build on the next visit instead of staying pinned to the old one.
//
// The native app skips this entirely: Capacitor already serves the bundle from
// the app package (updates ship with the app), so the SW has no job there and
// only reintroduces the stale-bundle and auth-callback interception bugs it has
// caused on web.
if (!isNativeApp()) {
  registerSW({ immediate: true });
}

const rootElement = document.querySelector('main');

// Render React render-phase errors (incl. the component stack) on-screen for
// mobile debugging. window.onerror only receives the JS call stack, which for a
// hooks-rule error like React #300 is all framework internals and never names
// the offending component — only errorInfo.componentStack does. Each entry
// carries a file:line:col so the culprit can be identified (deminified via the
// production source maps).
const showReactError = (label: string) => (error: unknown, errorInfo: { componentStack?: string }) => {
  const err = error as Error | undefined;
  document.body.innerHTML =
    `<pre style="padding:20px;color:red;font-size:12px;white-space:pre-wrap;">`
    + `${label}: ${err?.message ?? String(error)}\n\n`
    + `Component stack:\n${errorInfo?.componentStack || 'N/A'}\n\n`
    + `JS stack:\n${err?.stack || 'N/A'}</pre>`;
};

// The offline asset index holds absolute paths into the app's data container,
// which iOS moves on an app update. The store checks the container and rewrites
// the index before anything can resolve an asset URL against a stale one; if it
// cannot, it empties the index so the reader streams rather than fetching a
// path that no longer exists. Two bridge calls, well under the splash screen.
// Capped so a hung bridge cannot leave a blank screen; web builds have no store.
const STORE_READY_CAP_MS = 2000;
const prepareNativeStore = (): Promise<void> => {
  if (!isNativeApp()) {
    return Promise.resolve();
  }
  let settled = false;
  const ready = ensureStoreReady().then(
    () => undefined,
    () => undefined, // Already logged, and the index has been emptied.
  ).finally(() => {
    settled = true;
  });
  const cap = new Promise<void>((resolve) => {
    window.setTimeout(() => {
      if (!settled) {
        console.warn('[assetStore] Store readiness timed out; rendering anyway.');
      }
      resolve();
    }, STORE_READY_CAP_MS);
  });
  return Promise.race([ready, cap]);
};

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement, {
    onUncaughtError: showReactError('Uncaught render error'),
    onCaughtError: showReactError('Caught render error'),
  });
  void prepareNativeStore().then(() => {
    root.render(
      <React.StrictMode>
        {/*
          Above <App /> deliberately: when the server refuses this build, the
          upgrade screen replaces the whole tree rather than rendering inside a
          router and providers that may themselves be failing. Inert on web and
          on any native build the server has not refused.
        */}
        <ClientUpgradeGate>
          <App />
        </ClientUpgradeGate>
      </React.StrictMode>
    );
  });
}
